import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { getToken, setToken } from "@/lib/http";
import { authService } from "@/services/api";

const AuthContext = createContext(null);
const PIN_KEY = "nb_pin_email";
const UNLOCKED_KEY = "nb_unlocked";

export const pinEmail = () => localStorage.getItem(PIN_KEY);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon
  const [locked, setLocked] = useState(() => !!pinEmail() && !sessionStorage.getItem(UNLOCKED_KEY));

  useEffect(() => {
    if (!getToken()) {
      if (pinEmail()) { setLocked(true); setUser(false); return; }
      return setUser(false);
    }
    authService.me().then(setUser).catch(() => { setToken(null); setUser(false); });
  }, []);

  const finishAuth = (data) => {
    setToken(data.token);
    sessionStorage.setItem(UNLOCKED_KEY, "1");
    setLocked(false);
    setUser(data.user);
    return data.user;
  };

  const login = useCallback(async (email, password) => finishAuth(await authService.login(email, password)), []);
  const register = useCallback(async (data) => finishAuth(await authService.register(data)), []);
  const acceptInvite = useCallback(async (data) => finishAuth(await authService.acceptInvite(data)), []);
  const unlock = useCallback(async (pin) => finishAuth(await authService.unlockPin(pinEmail(), pin)), []);
  const loginWithPasskey = useCallback((data) => finishAuth(data), []);

  const enablePin = useCallback(async (pin, password) => {
    await authService.setPin(pin, password);
    localStorage.setItem(PIN_KEY, user.email);
    sessionStorage.setItem(UNLOCKED_KEY, "1");
    setUser((u) => ({ ...u, has_pin: true }));
  }, [user]);

  const disablePin = useCallback(async () => {
    await authService.removePin();
    localStorage.removeItem(PIN_KEY);
    setUser((u) => ({ ...u, has_pin: false }));
  }, []);

  const lock = useCallback(() => { sessionStorage.removeItem(UNLOCKED_KEY); setLocked(true); }, []);

  const logout = useCallback(async () => {
    try { await authService.logout(); } catch { /* ignore */ }
    setToken(null);
    localStorage.removeItem(PIN_KEY);
    sessionStorage.removeItem(UNLOCKED_KEY);
    setLocked(false);
    setUser(false);
  }, []);

  const forgetPin = useCallback(() => { localStorage.removeItem(PIN_KEY); setLocked(false); setToken(null); setUser(false); }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, acceptInvite, loginWithPasskey, logout, locked, unlock, lock, enablePin, disablePin, forgetPin, pinEnabled: !!pinEmail() }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
